import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_BOOKING, GET_RECEIVED_BOOKINGS, GET_SENT_BOOKINGS, GET_BOOKINGS } from '../../graphql/queries/booking';
import { UPDATE_BOOKING } from '../../graphql/mutations/booking';
import { CREATE_CLIENT } from '../../graphql/admin/mutations/client';
import { ArrowLeft, Package, Users, User, CreditCard, History } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';

const TYPES_CHAMBRE = [
  { value: 'single', label: 'Single' },
  { value: 'double', label: 'Double' },
  { value: 'triple', label: 'Triple' },
  { value: 'quadruple', label: 'Quadruple' },
  { value: 'quintuple', label: 'Quintuple' },
];

const PAIEMENT_OPTIONS = [
  { value: 'paye', label: 'Payé' },
  { value: 'avance', label: 'Avance' },
  { value: 'non_paye', label: 'Pas payé' },
];

const STATUT_OPTIONS = [
  { value: 'disponible', label: 'Disponible' },
  { value: 'attente', label: 'En attente de confirmation' },
  { value: 'non_disponible', label: 'Non disponible' },
];

type Personne = { type: 'adulte' | 'enfant'; nom: string; prenom: string; chambre: number };
type Bebe = { nom: string; prenom: string; chambre: number };
type Chambre = { type: string };

export default function EditBookingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const bookingIdNum = id && !isNaN(Number(id)) ? parseInt(id, 10) : 0;
  const { data, loading, error } = useQuery(GET_BOOKING, {
    variables: { id: bookingIdNum },
    skip: !id || isNaN(Number(id)),
  });
  const booking = data?.booking;
  const pkg = booking?.package;
  const [form, setForm] = useState<any>(null);
  const [showLoader, setShowLoader] = useState(false);
  const [createClient] = useMutation(CREATE_CLIENT);
  const [updateBooking, { loading: loadingUpdate, error: errorUpdate }] = useMutation(UPDATE_BOOKING);

  // Initialisation du formulaire quand la réservation est chargée
  useEffect(() => {
    if (booking && !form) {
      // Convertir les données de la réservation en format formulaire
      const personnes = booking.personnes && Array.isArray(booking.personnes) 
        ? booking.personnes.map((p: any) => ({
            type: p.type || 'adulte',
            nom: p.nom || '',
            prenom: p.prenom || '',
            chambre: p.chambre || 1,
          }))
        : [{ type: 'adulte', nom: booking.nom || '', prenom: booking.prenom || '', chambre: 1 }];
      
      const bebes = booking.bebes && Array.isArray(booking.bebes)
        ? booking.bebes.map((b: any) => ({
            nom: b.nom || '',
            prenom: b.prenom || '',
            chambre: b.chambre || 1,
          }))
        : [];
      
      const chambres = booking.chambres && Array.isArray(booking.chambres)
        ? booking.chambres.map((c: any) => ({
            type: c.type || 'double',
          }))
        : [{ type: 'double' }];

      setForm({
        nbrAdulte: booking.nbrAdulte || 1,
        nbrEnfant: booking.nbrEnfant || 0,
        nbrBebe: booking.nbrBebe || 0,
        nbrChambre: booking.nbrChambre || 1,
        nom: booking.nom || '',
        prenom: booking.prenom || '',
        telephone: booking.telephone || '',
        mail: booking.mail || '',
        typeReservation: booking.typeReservation || 'agence',
        formuleId: booking.formulaId || '',
        personnes,
        bebes,
        chambres,
        paiement: booking.paiement || 'non_paye',
        montantAvance: booking.montantAvance != null ? String(booking.montantAvance) : '',
        statutDisponibilite: booking.statutDisponibilite || 'attente',
      });
    }
  }, [booking]);

  // Fallback loader si form reste null plus de 200ms
  useEffect(() => {
    if (!form && booking) {
      const t = setTimeout(() => setShowLoader(true), 200);
      return () => clearTimeout(t);
    } else {
      setShowLoader(false);
    }
  }, [form, booking]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[40vh] text-gray-500">Chargement de la réservation...</div>
      </AdminLayout>
    );
  }
  if (error) {
    return (
      <AdminLayout>
        <div className="p-6 text-center text-red-600">Erreur lors du chargement de la réservation.</div>
      </AdminLayout>
    );
  }
  if (!booking) {
    return (
      <AdminLayout>
        <div className="p-6 text-center text-red-600">Réservation introuvable.</div>
      </AdminLayout>
    );
  }
  if (!pkg) {
    return (
      <AdminLayout>
        <div className="p-6 text-center text-red-600">Package introuvable.</div>
      </AdminLayout>
    );
  }
  if (!form && showLoader) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[40vh] text-gray-500">Initialisation du formulaire...</div>
      </AdminLayout>
    );
  }
  if (!form) return null;

  // Met à jour le tableau des personnes, bébés et chambres quand le nombre change
  const updatePersonnesEtBebesEtChambres = (adulteCount: number, enfantCount: number, bebeCount: number, chambreCount: number) => {
    const total = adulteCount + enfantCount;
    let personnes = [...form.personnes];
    if (personnes.length < total) {
      for (let i = personnes.length; i < total; i++) {
        personnes.push({
          type: i < adulteCount ? 'adulte' : 'enfant',
          nom: '',
          prenom: '',
          chambre: 1,
        });
      }
    } else if (personnes.length > total) {
      personnes = personnes.slice(0, total);
    }
    personnes = personnes.map((p, idx) => ({
      ...p,
      type: idx < adulteCount ? 'adulte' : 'enfant',
      chambre: p.chambre > chambreCount ? 1 : p.chambre || 1,
    }));
    // Gestion des bébés
    let bebes = form.bebes ? [...form.bebes] : [];
    if (bebes.length < bebeCount) {
      for (let i = bebes.length; i < bebeCount; i++) {
        bebes.push({ nom: '', prenom: '', chambre: 1 });
      }
    } else if (bebes.length > bebeCount) {
      bebes = bebes.slice(0, bebeCount);
    }
    bebes = bebes.map(b => ({ ...b, chambre: b.chambre > chambreCount ? 1 : b.chambre || 1 }));
    // Gestion des chambres
    let chambres = form.chambres ? [...form.chambres] : [];
    if (chambres.length < chambreCount) {
      for (let i = chambres.length; i < chambreCount; i++) {
        chambres.push({ type: 'double' });
      }
    } else if (chambres.length > chambreCount) {
      chambres = chambres.slice(0, chambreCount);
    }
    setForm(f => ({ ...f, personnes, nbrAdulte: adulteCount, nbrEnfant: enfantCount, nbrBebe: bebeCount, bebes, nbrChambre: chambreCount, chambres }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'nbrChambre') {
      updatePersonnesEtBebesEtChambres(form.nbrAdulte, form.nbrEnfant, form.nbrBebe, Math.max(1, parseInt(value) || 1));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'nbrAdulte' || name === 'nbrEnfant' || name === 'nbrBebe') {
      updatePersonnesEtBebesEtChambres(
        name === 'nbrAdulte' ? Math.max(1, parseInt(value) || 1) : form.nbrAdulte,
        name === 'nbrEnfant' ? Math.max(0, parseInt(value) || 0) : form.nbrEnfant,
        name === 'nbrBebe' ? Math.max(0, parseInt(value) || 0) : form.nbrBebe,
        form.nbrChambre
      );
    } else {
      setForm(f => ({ ...f, [name]: Math.max(0, parseInt(value) || 0) }));
    }
  };

  const handlePersonneChange = (idx: number, field: string, value: string | number) => {
    setForm(f => {
      const personnes = [...f.personnes];
      personnes[idx] = { ...personnes[idx], [field]: value };
      return { ...f, personnes };
    });
  };

  const handleBebeChange = (idx: number, field: string, value: string | number) => {
    setForm(f => {
      const bebes = [...(f.bebes || [])];
      bebes[idx] = { ...bebes[idx], [field]: value };
      return { ...f, bebes };
    });
  };

  const handleChambreTypeChange = (idx: number, value: string) => {
    setForm(f => {
      const chambres = [...f.chambres];
      chambres[idx] = { ...chambres[idx], type: value };
      return { ...f, chambres };
    });
  };

  // Fonction pour calculer le prix total selon le type de prix de la formule
  const calculateTotalPrice = (): number => {
    if (!pkg || !form.formuleId) return booking?.totalPrice || 0;
    
    const selectedFormula = Array.isArray(pkg.formulas)
      ? pkg.formulas.find((f: any) => f.id === form.formuleId)
      : booking?.formula;
    
    if (!selectedFormula) return booking?.totalPrice || 0;

    // Si prix par personne
    if (selectedFormula.priceType === 'PER_PERSON' && selectedFormula.perPersonPrice) {
      const totalPersons = form.nbrAdulte + form.nbrEnfant;
      return selectedFormula.perPersonPrice * totalPersons;
    }

    // Si prix par chambre
    if (selectedFormula.priceType === 'PER_ROOM' && selectedFormula.roomPrices) {
      let totalPrice = 0;
      form.chambres.forEach((chambre: any) => {
        const roomType = chambre.type;
        const roomPrice = selectedFormula.roomPrices[roomType] || 0;
        totalPrice += roomPrice;
      });
      return totalPrice;
    }

    return booking?.totalPrice || 0;
  };

  // Calculer le prix total directement
  const totalPrice = calculateTotalPrice();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!booking || !form) return;

    // 1. Créer ou mettre à jour le client
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const agencyId = user.agencyId || user.agency?.id;

    // Utiliser la première personne du formulaire comme client principal
    const mainPerson = form.personnes && form.personnes.length > 0 ? form.personnes[0] : { nom: form.nom || '', prenom: form.prenom || '' };
    if (!mainPerson.nom || !mainPerson.prenom) {
      alert('Le nom et le prénom du voyageur principal sont obligatoires.');
      return;
    }

    let clientId = booking.clientId;
    // Si les informations du client ont changé, créer un nouveau client
    if (mainPerson.nom !== booking.client?.firstName || mainPerson.prenom !== booking.client?.lastName || form.mail !== booking.client?.email || form.telephone !== booking.client?.phone) {
      const clientInput = {
        firstName: mainPerson.nom,
        lastName: mainPerson.prenom,
        email: form.mail,
        phone: form.telephone,
        address: '',
        agencyId,
      };
      try {
        const { data: clientData } = await createClient({ variables: { input: clientInput } });
        clientId = clientData?.createClient?.id || booking.clientId;
      } catch (err) {
        console.error('Erreur lors de la création/mise à jour du client:', err);
        // Continuer avec l'ancien clientId si la création échoue
      }
    }

    // 2. Calculer le prix total
    const calculatedTotalPrice = calculateTotalPrice();
    
    // 3. Mettre à jour la réservation
    const { formuleId, ...restForm } = form;
    const input = {
      ...restForm,
      packageId: booking.packageId,
      formulaId: formuleId || booking.formulaId,
      travelerCount: form.nbrAdulte + form.nbrEnfant + form.nbrBebe,
      totalPrice: calculatedTotalPrice,
      contactInfo: {
        nom: form.nom,
        prenom: form.prenom,
        telephone: form.telephone,
        mail: form.mail,
      },
      clientId: clientId || booking.clientId,
      montantAvance: form.montantAvance === '' ? null : Number(form.montantAvance),
      montantPaye: form.montantPaye === '' ? null : Number(form.montantPaye),
    };

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const agencyId = user.agencyId ?? user.agency?.id ?? booking?.package?.agency?.id ?? pkg?.agency?.id;
      await updateBooking({
        variables: { id: parseInt(id!), input },
        refetchQueries: agencyId
          ? [
              { query: GET_RECEIVED_BOOKINGS, variables: { agencyId } },
              { query: GET_SENT_BOOKINGS, variables: { agencyId } },
              { query: GET_BOOKINGS, variables: { agencyId } },
            ]
          : [],
        awaitRefetchQueries: true
      });
      alert('Réservation mise à jour avec succès');
      const packageId = booking.packageId ?? booking.package?.id ?? pkg?.id;
      navigate(packageId ? `/admin/packages/${packageId}/clients` : '/admin/bookings');
    } catch (err: any) {
      alert('Erreur lors de la mise à jour de la réservation: ' + (err.message || 'Erreur inconnue'));
    }
  };

  const formulas = (pkg?.formulas && Array.isArray(pkg.formulas)) ? pkg.formulas : (booking?.formula ? [booking.formula] : []);

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 w-fit"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          {id && (
            <Link
              to={`/admin/bookings/${id}/history`}
              className="inline-flex items-center justify-center gap-2 text-sm font-medium text-indigo-700 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg px-4 py-2 transition-colors w-fit"
            >
              <History className="w-4 h-4 shrink-0" />
              Historique des modifications
            </Link>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
            <h1 className="text-xl font-semibold text-gray-900">Modifier la réservation</h1>
            <p className="text-sm text-gray-500 mt-0.5">{pkg.title}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Séjour */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-indigo-600" />
              <h2 className="font-semibold text-gray-900">Séjour</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Formule</label>
                <select
                  name="formuleId"
                  value={form.formuleId}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {formulas.length > 0 ? (
                    formulas.map((f: any) => (
                      <option key={f.id} value={f.id}>
                        {f.name} {f.perPersonPrice ? `— ${Number(f.perPersonPrice).toLocaleString('fr-FR')} DA` : ''}
                      </option>
                    ))
                  ) : (
                    <option disabled>Aucune formule disponible</option>
                  )}
                </select>
              </div>
              <input type="hidden" name="typeReservation" value={form.typeReservation} />
            </div>
          </div>

          {/* Voyageurs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-indigo-600" />
              <h2 className="font-semibold text-gray-900">Voyageurs & chambres</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adultes</label>
                <input type="number" name="nbrAdulte" min={1} value={form.nbrAdulte} onChange={handleNumberChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Enfants</label>
                <input type="number" name="nbrEnfant" min={0} value={form.nbrEnfant} onChange={handleNumberChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bébés</label>
                <input type="number" name="nbrBebe" min={0} value={form.nbrBebe} onChange={handleNumberChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Chambres</label>
                <input type="number" name="nbrChambre" min={1} value={form.nbrChambre} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500" required />
              </div>
            </div>
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Types de chambres</h3>
              <div className="flex flex-wrap gap-3">
                {form.chambres.map((ch: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Chambre {idx + 1}</span>
                    <select value={ch.type} onChange={(e) => handleChambreTypeChange(idx, e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                      {TYPES_CHAMBRE.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-6 space-y-4">
              {form.personnes.map((p: Personne, idx: number) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                  <div className="flex flex-wrap gap-4 items-end">
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-xs font-medium text-gray-500 mb-1">{p.type === 'adulte' ? `Adulte ${idx + 1}` : `Enfant ${idx + 1 - form.nbrAdulte}`}</label>
                      <div className="flex gap-2">
                        <input type="text" placeholder="Nom" value={p.nom} onChange={(e) => handlePersonneChange(idx, 'nom', e.target.value)} className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" required />
                        <input type="text" placeholder="Prénom" value={p.prenom} onChange={(e) => handlePersonneChange(idx, 'prenom', e.target.value)} className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" required />
                      </div>
                    </div>
                    <div className="w-36 min-w-[8rem]">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Chambre</label>
                      <select value={p.chambre} onChange={(e) => handlePersonneChange(idx, 'chambre', parseInt(e.target.value))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        {form.chambres.map((_: any, cidx: number) => <option key={cidx} value={cidx + 1}>Chambre {cidx + 1}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {form.bebes && form.bebes.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Bébés</h3>
                <div className="space-y-3">
                  {form.bebes.map((b: Bebe, idx: number) => (
                    <div key={idx} className="flex flex-wrap gap-4 items-end border border-gray-200 rounded-lg p-3 bg-gray-50/30">
                      <div className="flex-1 min-w-[200px] flex gap-2">
                        <input type="text" placeholder="Nom" value={b.nom} onChange={(e) => handleBebeChange(idx, 'nom', e.target.value)} className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" required />
                        <input type="text" placeholder="Prénom" value={b.prenom} onChange={(e) => handleBebeChange(idx, 'prenom', e.target.value)} className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" required />
                      </div>
                      <div className="w-36 min-w-[8rem]">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Chambre</label>
                        <select value={b.chambre} onChange={(e) => handleBebeChange(idx, 'chambre', parseInt(e.target.value))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                          {form.chambres.map((_: any, cidx: number) => <option key={cidx} value={cidx + 1}>Chambre {cidx + 1}</option>)}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Contact */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-indigo-600" />
              <h2 className="font-semibold text-gray-900">Contact</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input type="tel" name="telephone" value={form.telephone} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" name="mail" value={form.mail} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500" required />
              </div>
            </div>
          </div>

          {/* Paiement */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-indigo-600" />
              <h2 className="font-semibold text-gray-900">Paiement</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <select name="paiement" value={form.paiement} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500">
                  {PAIEMENT_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
              {form.paiement === 'avance' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Montant de l'avance (DA)</label>
                  <input type="number" name="montantAvance" value={form.montantAvance} onChange={handleChange} min={0} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500" />
                </div>
              )}
            </div>
          </div>

          <input type="hidden" name="statutDisponibilite" value={form.statutDisponibilite} />
          <input type="hidden" name="packageId" value={pkg.id} />

          {/* Récap prix + submit */}
          <div className="bg-gray-50 rounded-xl border border-gray-100 p-6">
            {(() => {
              const selectedFormula = formulas.find((f: any) => f.id === form.formuleId) || booking?.formula;
              if (!selectedFormula) return null;
              const unitPrice = selectedFormula.priceType === 'PER_PERSON' ? selectedFormula.perPersonPrice : null;
              const roomPriceDetails = selectedFormula.priceType === 'PER_ROOM'
                ? form.chambres.map((ch: any, idx: number) => ({ type: ch.type, price: selectedFormula.roomPrices?.[ch.type] || 0, index: idx + 1 }))
                : null;
              return (
                <>
                  <div className="space-y-2 mb-4">
                    {selectedFormula.priceType === 'PER_PERSON' && unitPrice && (
                      <>
                        <div className="flex justify-between text-sm text-gray-600"><span>Prix par personne</span><span>{Number(unitPrice).toLocaleString('fr-FR')} DA</span></div>
                        <div className="flex justify-between text-sm text-gray-600"><span>Personnes (adultes + enfants)</span><span>{form.nbrAdulte + form.nbrEnfant}</span></div>
                      </>
                    )}
                    {selectedFormula.priceType === 'PER_ROOM' && roomPriceDetails && (
                      <>
                        <div className="text-xs text-gray-500 mb-1">Prix par personne selon le type de chambre</div>
                        {roomPriceDetails.map((room: any) => (
                          <div key={room.index} className="flex justify-between text-sm text-gray-600"><span>Chambre {room.index} ({room.type})</span><span>{room.price.toLocaleString('fr-FR')} DA / pers.</span></div>
                        ))}
                      </>
                    )}
                  </div>
                  <div className="flex justify-between items-center border-t border-gray-200 pt-4">
                    <span className="font-semibold text-gray-900">Prix total</span>
                    <span className="text-xl font-semibold text-indigo-600">{totalPrice.toLocaleString('fr-FR')} DA</span>
                  </div>
                </>
              );
            })()}
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button type="button" onClick={() => navigate(-1)} className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Annuler</button>
            <button type="submit" disabled={loadingUpdate} className="px-6 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition">
              {loadingUpdate ? 'Mise à jour en cours...' : 'Mettre à jour la réservation'}
            </button>
          </div>
          {errorUpdate && <div className="text-red-600 text-sm text-center">Erreur lors de la mise à jour de la réservation.</div>}
        </form>
      </div>
    </AdminLayout>
  );
}
